/**
 * TenantConfigProvider — fetches runtime config and applies brand
 * tokens / fonts / metadata / i18n.
 *
 * Behaviour
 * ---------
 *  - GET `${env.VITE_TENANT_API_URL}/config` (host header resolves the
 *    tenant on the engine side).
 *  - Exponential backoff with jitter on 5xx + network errors. 3 retries
 *    on top of the first attempt, base 500ms doubling each time, ±100ms
 *    jitter. NEVER retries on 4xx — those become discrete render states
 *    so the operator can see *why* (auth missing, host not configured).
 *  - Discrete states: loading | live | paused | archived | not-found |
 *    error (with `canRetry`).
 *  - `AbortController` cancels in-flight fetches on unmount and on
 *    refetch. Aborts never bubble to the user — they short-circuit the
 *    setState path.
 *  - Dev-only mock fallback: when `import.meta.env.DEV` and
 *    `env.VITE_MOCK_TENANT_CONFIG_URL` is set, the provider tries the
 *    live tenant API first; if every attempt fails, it falls back to
 *    the mock URL with a `reportEvent('tenant-config:mock-fallback')`
 *    log line so the dev knows fixtures are driving the UI.
 *  - Side effects (CSS tokens, fonts, metadata, i18n) only fire on
 *    transitions into `live` or `paused`. Same effect set in both
 *    states so the operator notice in `paused` still ships the brand
 *    surface around it.
 *  - All children are wrapped in `TenantErrorBoundary` so a render-time
 *    throw below this provider becomes a friendly fallback rather than
 *    a blank document.
 */

import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { RuntimeConfig } from '@aamp/shared-types';
import { env } from '../env.js';
import { initI18n } from '../i18n/index.js';
import { reportError, reportEvent } from '../observability.js';
import { TenantErrorBoundary } from './TenantErrorBoundary.js';
import type { TenantStatus } from './states.js';

export type { TenantStatus } from './states.js';

export const TenantConfigContext = createContext<TenantStatus>({ kind: 'loading' });
export const TenantRetryContext = createContext<() => void>(() => {});
/**
 * Imperative refresh hook — called by other providers (notably the
 * engine client) when the runtime config has gone stale on the server
 * side, e.g. a rotated `publicApiKey` that just produced a 401. Fires
 * a fresh `/config` fetch without going through the user-visible
 * loading state. Debounced inside the provider so callers cannot
 * accidentally hammer the engine.
 */
export const TenantRefreshContext = createContext<() => void>(() => {});

/** 3 retries: 500ms, 1000ms, 2000ms (±100ms jitter applied per call). */
const RETRY_DELAYS_MS = [500, 1000, 2000] as const;
const JITTER_MS = 100;

interface Props {
  children: ReactNode;
  /**
   * Test seam — overrides `globalThis.fetch`. Production code never
   * passes this; tests inject msw or a stub directly.
   */
  fetchImpl?: typeof fetch;
}

function jitter(base: number): number {
  // Symmetric ±JITTER_MS around `base`. `Math.random()` is fine here —
  // this is timing noise, not anything load-bearing security-wise.
  return base + (Math.random() * 2 - 1) * JITTER_MS;
}

/**
 * Wrap an async unit of work with an abort listener that is GUARANTEED
 * to be detached in `finally` — both on resolve and reject, regardless
 * of whether the abort fired. `signal.addEventListener('abort', …, {
 * once: true })` would still leave the listener attached on quick
 * resolves until the AbortController itself is GC'd; this helper
 * removes the listener deterministically.
 *
 * `work` receives a callable that throws an `AbortError` synchronously
 * if the signal is already aborted. Use it at await points if the
 * caller wants to short-circuit on abort without bubbling a fetch
 * error first.
 */
function abortablePromise<T>(
  signal: AbortSignal,
  work: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const onAbort = (): void => {
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort);
    work(signal).then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (err: unknown) => {
        signal.removeEventListener('abort', onAbort);
        reject(err instanceof Error ? err : new Error(String(err)));
      },
    );
  });
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  // `abortablePromise` rejects the outer promise on abort and removes
  // its listener in `finally`. We additionally need to clear the timer
  // so a pending setTimeout doesn't leak past the abort. The inner
  // listener is `once: true`-bounded by the timer-fired path: when
  // `setTimeout` resolves, we clear the listener manually.
  return abortablePromise<void>(signal, (sig) => {
    return new Promise<void>((resolve) => {
      const id = setTimeout(() => {
        sig.removeEventListener('abort', clearTimer);
        resolve();
      }, ms);
      const clearTimer = (): void => {
        clearTimeout(id);
      };
      sig.addEventListener('abort', clearTimer, { once: true });
    });
  });
}

interface FetchOutcome {
  /** Final discrete state computed from the HTTP response or error. */
  state: TenantStatus;
  /** True if every attempt failed with a transient cause (5xx / network). */
  transientFailure: boolean;
}

async function fetchOnce(
  fetchImpl: typeof fetch,
  url: string,
  signal: AbortSignal,
): Promise<FetchOutcome> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const response = await abortablePromise(signal, (sig) =>
        fetchImpl(url, { credentials: 'same-origin', signal: sig }),
      );

      if (response.status === 404) {
        return { state: { kind: 'not-found' }, transientFailure: false };
      }
      if (response.status === 410) {
        return { state: { kind: 'archived' }, transientFailure: false };
      }
      if (response.status >= 400 && response.status < 500) {
        // 4xx is structurally wrong — host not configured, auth missing,
        // bad request. Retrying won't fix it.
        return {
          state: {
            kind: 'error',
            error: new Error(`Tenant config rejected: HTTP ${response.status}`),
            canRetry: false,
          },
          transientFailure: false,
        };
      }
      if (response.status >= 500) {
        lastError = new Error(`Tenant config 5xx: HTTP ${response.status}`);
        // fall through to retry / final-error below
      } else if (response.ok) {
        const config = (await response.json()) as RuntimeConfig;
        if (config.tenant.status === 'paused') {
          return { state: { kind: 'paused', config }, transientFailure: false };
        }
        return { state: { kind: 'live', config }, transientFailure: false };
      } else {
        // 3xx is unexpected from a JSON API; treat as a hard error.
        return {
          state: {
            kind: 'error',
            error: new Error(`Unexpected HTTP ${response.status}`),
            canRetry: false,
          },
          transientFailure: false,
        };
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw err;
      }
      lastError = err instanceof Error ? err : new Error('Unknown network error');
    }

    const baseDelay = RETRY_DELAYS_MS[attempt];
    if (baseDelay === undefined) {
      break;
    }
    await delay(jitter(baseDelay), signal);
  }

  return {
    state: {
      kind: 'error',
      error: lastError ?? new Error('Tenant config fetch failed'),
      canRetry: true,
    },
    transientFailure: true,
  };
}

async function fetchTenantConfig(
  fetchImpl: typeof fetch,
  signal: AbortSignal,
): Promise<TenantStatus> {
  const liveUrl = `${env.VITE_TENANT_API_URL}/config`;
  const liveOutcome = await fetchOnce(fetchImpl, liveUrl, signal);

  // Dev fallback: only when DEV mode AND the operator has set a mock
  // URL AND every live attempt failed transiently. We never fall back
  // on 4xx — that's a config error the dev should fix.
  const mockUrl = env.VITE_MOCK_TENANT_CONFIG_URL;
  if (
    import.meta.env.DEV &&
    mockUrl !== undefined &&
    mockUrl !== '' &&
    liveOutcome.transientFailure
  ) {
    reportEvent('tenant-config:mock-fallback', { mockUrl });
    const mockOutcome = await fetchOnce(fetchImpl, mockUrl, signal);
    return mockOutcome.state;
  }

  return liveOutcome.state;
}

/**
 * Snapshot of every artefact the provider has poured into the document
 * for the *current* tenant. On rotation we use this to revert each one
 * before applying the next config so the document doesn't accumulate
 * stale CSS variables, fonts, preloads, or favicon links across tenant
 * switches in the same tab (preview/multi-host dev, mostly).
 */
interface AppliedArtefacts {
  tokenKeys: Set<string>;
  fontFaces: FontFace[];
  preloadLinks: HTMLLinkElement[];
  faviconLink: HTMLLinkElement | null;
}

function emptyArtefacts(): AppliedArtefacts {
  return {
    tokenKeys: new Set<string>(),
    fontFaces: [],
    preloadLinks: [],
    faviconLink: null,
  };
}

/** Minimum gap between consecutive `refresh()` calls (auth-failure debounce). */
const REFRESH_DEBOUNCE_MS = 30_000;

export function TenantConfigProvider({ children, fetchImpl }: Props): ReactElement {
  const [state, setState] = useState<TenantStatus>({ kind: 'loading' });
  const [refetchToken, setRefetchToken] = useState(0);

  // Snapshot of artefacts applied for the previously-rendered tenant so
  // we can revert them before applying the new ones (H1 — tenant rotation
  // hygiene). Lives on a ref because we mutate it from inside the effect
  // *and* from cleanup; we never want to trigger a re-render off it.
  const artefactsRef = useRef<AppliedArtefacts>(emptyArtefacts());
  // Last time `refresh()` actually fired a fetch. Debounces back-to-back
  // 401-driven refreshes to at most one per `REFRESH_DEBOUNCE_MS`.
  const lastRefreshRef = useRef<number>(0);

  const retry = useCallback((): void => {
    setState({ kind: 'loading' });
    setRefetchToken((n) => n + 1);
  }, []);

  const refresh = useCallback((): void => {
    const now = Date.now();
    if (now - lastRefreshRef.current < REFRESH_DEBOUNCE_MS) {
      reportEvent('tenant-config:refresh-debounced', {
        sinceLastMs: now - lastRefreshRef.current,
      });
      return;
    }
    lastRefreshRef.current = now;
    reportEvent('tenant-config:refresh-requested', {});
    // Don't drop into `loading` — refresh is a silent re-check and we
    // want the existing surface to keep rendering until the new config
    // (or the same one) lands.
    setRefetchToken((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const fetchFn = fetchImpl ?? globalThis.fetch.bind(globalThis);

    void (async (): Promise<void> => {
      try {
        const next = await fetchTenantConfig(fetchFn, controller.signal);
        if (controller.signal.aborted) {
          return;
        }
        setState(next);

        if (next.kind === 'live' || next.kind === 'paused') {
          // Revert anything from a previous tenant first, then apply.
          revertArtefacts(artefactsRef.current);
          const fresh = emptyArtefacts();
          applyBrandTokens(next.config.brand.tokens, fresh);
          loadBrandFonts(next.config.brand.fonts, fresh);
          applyMetadata(next.config, fresh);
          artefactsRef.current = fresh;
          await initI18n(next.config.market.locale, next.config.i18n?.strings).catch((err: unknown) => {
            reportError(
              err instanceof Error ? err : new Error('i18n init failed'),
              { scope: 'tenant-provider', step: 'initI18n' },
            );
          });
        } else if (next.kind === 'error') {
          reportError(next.error, {
            scope: 'tenant-provider',
            canRetry: next.canRetry,
          });
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        if (controller.signal.aborted) {
          return;
        }
        const error = err instanceof Error ? err : new Error('Unknown error');
        reportError(error, { scope: 'tenant-provider', step: 'fetch-loop' });
        setState({ kind: 'error', error, canRetry: true });
      }
    })();

    return (): void => {
      controller.abort();
    };
  }, [fetchImpl, refetchToken]);

  // On unmount, revert any artefacts still on the document. The render
  // tree shouldn't outlive its tenant, but a misuse of the provider in
  // a Storybook / Playground environment could leave the host doc dirty.
  useEffect(() => {
    return (): void => {
      revertArtefacts(artefactsRef.current);
      artefactsRef.current = emptyArtefacts();
    };
  }, []);

  return (
    <TenantConfigContext.Provider value={state}>
      <TenantRetryContext.Provider value={retry}>
        <TenantRefreshContext.Provider value={refresh}>
          <TenantErrorBoundary>{children}</TenantErrorBoundary>
        </TenantRefreshContext.Provider>
      </TenantRetryContext.Provider>
    </TenantConfigContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Side effects
// ---------------------------------------------------------------------------
// Each `apply*` helper writes into the document AND records what it did
// onto the `tracker` so a subsequent `revertArtefacts(tracker)` can
// remove every CSS var, FontFace, preload <link>, and favicon link the
// previous tenant brought in. This matters when the same bundle serves
// different tenants in the same tab (preview / multi-host dev). In a
// single-tenant production deployment the revert is a no-op.

function applyBrandTokens(
  tokens: Record<string, string>,
  tracker: AppliedArtefacts,
): void {
  const root = document.documentElement;
  for (const [key, value] of Object.entries(tokens)) {
    root.style.setProperty(key, value);
    tracker.tokenKeys.add(key);
  }
}

function loadBrandFonts(
  fonts: RuntimeConfig['brand']['fonts'],
  tracker: AppliedArtefacts,
): void {
  // P13 — preload + `font-display: swap`:
  //   1. Issue a `<link rel="preload" as="font">` for each font BEFORE
  //      kicking off the FontFace load. This gives the browser a head
  //      start over the JS-driven loader so the first text paint hits
  //      with the brand font already in cache.
  //   2. `display: 'swap'` on every FontFace so glyph rendering never
  //      blocks layout — the browser shows the fallback (system or
  //      web-safe stack) until the brand font is decoded, then swaps.
  //      Removes 100–500ms of FOIT on slow connections.
  for (const font of fonts) {
    const link = preloadFontLink(font.url);
    if (link) tracker.preloadLinks.push(link);
    const fontFace = new FontFace(font.family, `url(${font.url})`, {
      weight: String(font.weight),
      style: font.style,
      display: 'swap',
    });
    tracker.fontFaces.push(fontFace);
    fontFace
      .load()
      .then((loaded) => document.fonts.add(loaded))
      .catch((err: unknown) => {
        reportError(
          err instanceof Error ? err : new Error(`Failed to load font ${font.family}`),
          { scope: 'tenant-provider', step: 'loadBrandFonts', family: font.family },
        );
      });
  }
}

function preloadFontLink(url: string): HTMLLinkElement | null {
  // Already-preloaded URLs are skipped to avoid duplicate <link> nodes.
  // The existing node was attributed to the previous tenant's tracker
  // and will be removed when that tracker is reverted.
  const existing = document.head.querySelector<HTMLLinkElement>(
    `link[rel="preload"][as="font"][href="${url}"]`,
  );
  if (existing) return null;

  // Best-effort MIME type from the URL extension. WOFF2 is the only
  // format the contract guarantees; anything else falls through with
  // an empty `type` (browser sniffs from the response headers).
  const lower = url.toLowerCase();
  let type = '';
  if (lower.endsWith('.woff2')) type = 'font/woff2';
  else if (lower.endsWith('.woff')) type = 'font/woff';
  else if (lower.endsWith('.ttf')) type = 'font/ttf';

  const link = document.createElement('link');
  link.rel = 'preload';
  // Use `setAttribute` — `link.as = 'font'` is the IDL accessor and
  // some test environments (jsdom) don't reflect it back to the HTML
  // attribute, which then breaks attribute-selector queries.
  link.setAttribute('as', 'font');
  link.href = url;
  link.crossOrigin = 'anonymous';
  if (type) link.type = type;
  document.head.appendChild(link);
  return link;
}

function applyMetadata(
  config: RuntimeConfig,
  tracker: AppliedArtefacts,
): void {
  document.title = config.metadata.title;
  document.documentElement.lang = config.market.locale;

  const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (existing) {
    existing.href = config.brand.logos.favicon;
    // We didn't create this link, so don't track it — let the host page
    // keep ownership when we eventually unmount.
    tracker.faviconLink = null;
  } else {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = config.brand.logos.favicon;
    document.head.appendChild(link);
    tracker.faviconLink = link;
  }
}

/**
 * Undo every artefact the previous `apply*` pass installed. Safe to
 * call against `emptyArtefacts()` — every loop body is empty in that
 * case. Removal order doesn't matter; each artefact is independent.
 */
function revertArtefacts(snapshot: AppliedArtefacts): void {
  const root = document.documentElement;
  for (const key of snapshot.tokenKeys) {
    root.style.removeProperty(key);
  }
  if (snapshot.fontFaces.length > 0) {
    const fontSet = document.fonts;
    for (const face of snapshot.fontFaces) {
      try {
        fontSet.delete(face);
      } catch (err: unknown) {
        // FontFaceSet.delete throws if the face was never added — that
        // happens on URLs that failed to load. Swallow with a debug
        // event so we still see it in dev observability.
        reportEvent('tenant-config:font-revert-failed', {
          family: face.family,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    }
  }
  for (const link of snapshot.preloadLinks) {
    link.remove();
  }
  if (snapshot.faviconLink) {
    snapshot.faviconLink.remove();
  }
}
